import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Row, Col } from 'react-bootstrap';
import { FaQuestionCircle } from 'react-icons/fa';

import ModalRaw from 'v3/components/Modal';
import Button from 'v3/components/Button';
import Text from 'v3/components/Text';
import InputCurrency from 'v3/components/Input/Currency';
import Input from 'v3/components/Input';
import Radio from 'v3/components/Radio';
import Tooltip from 'v3/components/Tooltip';
import IconButton from 'v3/components/Button/Icon';

import theme from 'v3/theme';
import { convertToBRLValue, convertToDollarValue } from 'v3/utils/formatter';
import { CalculatorButton } from '../Calculator';
import ValidatorExtraFields from './validator';
import { BRL_PRECISION, USD_PRECISION } from '../../context';

export default function ExtraFieldsModal({
  extraFields,
  isOpen,
  handleClose,
  onSave,
  isBRLCurrency,
  dollarToUse,
}) {
  const [errors, setErrors] = useState([]);
  const [fields, setFields] = useState([
    {
      label: '',
      value: '0',
      dollarValue: '0',
      showToDriver: false,
    },
  ]);

  useEffect(() => {
    if (isOpen) {
      if (extraFields?.length > 0) {
        setFields(
          extraFields?.map((item) => {
            return {
              label: item.label,
              value: item.value,
              dollarValue: item.dollarValue,
              showToDriver: item.showToDriver,
            };
          }),
        );
      } else {
        setFields([
          {
            label: '',
            value: '0',
            dollarValue: '0',
            showToDriver: false,
          },
        ]);
      }
    }
  }, [isOpen, extraFields]);

  function handleDeleteCard(idx) {
    const updated = fields.filter((_, index) => index !== idx);
    setFields(updated);
  }

  function handleChange(index, key, value) {
    setFields((old) =>
      old.map((item, idx) => {
        if (index === idx) {
          return { ...item, [key]: value };
        }
        return item;
      }),
    );
  }

  function handleChangeCalculator(index, newValue) {
    setFields((old) =>
      old.map((item, idx) => {
        if (index === idx) {
          return {
            ...item,
            value: newValue?.value,
            dollarValue: newValue?.dollarValue,
          };
        }
        return item;
      }),
    );
  }

  function handleChangeValue(index, nextValue) {
    setFields((old) =>
      old.map((item, idx) => {
        if (index === idx) {
          let value;
          let dollarValue;
          if (isBRLCurrency) {
            value = nextValue;
            dollarValue = convertToDollarValue(nextValue, dollarToUse, USD_PRECISION);
          } else {
            value = convertToBRLValue(nextValue, dollarToUse, BRL_PRECISION);
            dollarValue = nextValue;
          }

          return {
            ...item,
            value,
            dollarValue,
          };
        }
        return item;
      }),
    );
  }

  const hasDuplicates = useMemo(() => {
    const auxArray = [...fields?.map((item) => item.label)];
    return new Set(auxArray).size !== auxArray.length;
  }, [fields]);

  async function handleSave() {
    try {
      if (fields.length > 0) {
        await ValidatorExtraFields.validate(fields, {
          abortEarly: false,
        });
      }

      if (hasDuplicates) {
        return;
      }

      onSave(fields);
      setErrors({});
      handleClose();
    } catch (error) {
      if (error.inner) {
        error.inner.forEach((value) =>
          setErrors((old) => ({ ...old, [value.path]: value.message })),
        );
      }
    }
  }

  function handleAddField() {
    setErrors({});

    setFields((old) => [
      ...old,
      {
        label: '',
        value: '0',
        showToDriver: false,
      },
    ]);
  }

  const currency = useMemo(() => {
    return isBRLCurrency ? 'R$' : '$';
  }, [isBRLCurrency]);

  const renderItems = useCallback(() => {
    return (
      <>
        {fields.map((item, index) => (
          <>
            <Row className="form ">
              <Col md={6}>
                <Input
                  label="Nome do valor"
                  error={errors[`[${index}].label`]}
                  value={item.label}
                  onChange={(event) => {
                    handleChange(index, 'label', event.target.value);
                  }}
                  id={`input-nome-valor-${index}`}
                />
              </Col>
              <Col md={3}>
                <InputCurrency
                  label={`Valor (${currency})`}
                  error={errors[`[${index}].value`]}
                  value={isBRLCurrency ? item.value : item.dollarValue}
                  onChange={(_event, value) => {
                    handleChangeValue(index, value);
                  }}
                  id={`input-valor-${index}`}
                />
              </Col>
              <Col
                md={3}
                className="d-flex align-items-baseline mt-4 text-right"
                style={{ gap: '0.5rem' }}
              >
                <CalculatorButton
                  dollarToUse={dollarToUse}
                  id={`button-calculadora-valor-extra-carga-${index}`}
                  isBRLCurrency={isBRLCurrency}
                  onChange={(event, value) => {
                    handleChangeCalculator(index, value);
                  }}
                />
                <IconButton
                  style={{ marginTop: 13 }}
                  variant="error"
                  icon="FaTrash"
                  color="white"
                  onClick={() => handleDeleteCard(index)}
                  id={`button-limpar-valor-extra-${index}`}
                />
              </Col>

              <Col md={4} className="mt-3">
                <Radio.Group
                  label="Mostrar campo para motoristas"
                  onChange={(event) => {
                    handleChange(index, 'showToDriver', event.target.value);
                  }}
                  value={item.showToDriver}
                  horizontal
                >
                  <Radio id={`radio-mostrar-motorista-sim-${index}`} value>
                    <Text type="label">Sim</Text>
                  </Radio>
                  <Radio id={`radio-mostrar-motorista-nao-${index}`} value={false}>
                    <Text color="dark" type="label">
                      Não
                    </Text>
                  </Radio>
                </Radio.Group>
              </Col>
            </Row>
            <hr style={{ marginTop: 15, marginBottom: 30 }} />
          </>
        ))}
        <div md={3} className="mb-4">
          <Button
            variant="success"
            className="py-2"
            onClick={() => {
              handleAddField();
            }}
            disabled={fields.length >= 15}
            title={fields.length >= 15 ? 'Máximo de 15 valores' : ''}
            id="button-adicionar-valor-extra"
          >
            <Text weight={500} type="regular">
              Adicionar
            </Text>
          </Button>
        </div>
        {hasDuplicates && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text weight="500" type="regular" color="danger">
              Algum nome informado está repetido!
            </Text>
          </div>
        )}
      </>
    );
  }, [fields, errors, hasDuplicates, currency]);

  return (
    <>
      <ModalRaw
        show={isOpen}
        onHide={handleClose}
        size="lg"
        heading={
          <Text
            type="subheader"
            color="dark"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <span>Campos extras da carga</span>
            <Tooltip
              placement="right"
              content={<Text type="label">Valor extra que será adicionado à carga.</Text>}
            >
              <FaQuestionCircle color={theme.colors.light_blue} />
            </Tooltip>
          </Text>
        }
        body={renderItems()}
        footer={
          <Row md={12} className="mt-1">
            <Button className="py-2" variant="primary" onClick={() => handleSave()}>
              <Text weight="500" type="regular">
                Salvar
              </Text>
            </Button>
          </Row>
        }
      />
    </>
  );
}
